import { HOW_TO_USE_SEARCH_PAYLOAD_VIDEO_URL } from '@aibees/elephant/constants';
import {
  DialogTitle,
  Icon,
  JsonEditor,
  Label,
  VideoModal,
} from '@aibees/shared/components';
import {
  useBoolean,
  useCopyToClipboard,
  useNotify,
} from '@aibees/shared/hooks';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { memo, ReactNode, useMemo, useState } from 'react';
import * as SX from './sx';
import { useSearchPayload } from './use-search-payload';

type Props = {
  state: ReturnType<typeof useBoolean>;
};

type TabValue = 'filters' | 'api';

/** Alert copy per tab; the API tab links "AI Ark API" to the scope's
 *  API-reference page (company vs people search). */
const ALERT_TEXT: Record<TabValue, (apiDocsUrl: string) => ReactNode> = {
  filters: () =>
    'This is your current search as editable JSON, for use inside the app. ' +
    'Copy it, tweak the values — an AI agent can do this for you — then ' +
    'paste it back and click Apply Search. Keep the field names and ' +
    'structure intact. To run the same search via our API, see the API ' +
    'Payload tab.',
  api: (apiDocsUrl) => (
    // Single <span> child: the themed Alert message is a flex column, so
    // multiple children would each land on their own line.
    <span>
      {'This is the same search written for the '}
      <Link
        href={apiDocsUrl}
        target="_blank"
        rel="noopener noreferrer"
        underline="always"
        sx={SX.docsLink}
      >
        AI Ark API
      </Link>
      {" — method, URL and body. It's read-only. Use it to call the API " +
        'directly, or hand it to an AI agent and ask it to build the ' +
        'integration. To tweak filters and re-run the search here inside ' +
        'the app, switch to the In-App Payload tab, which uses a different ' +
        'structure.'}
    </span>
  ),
};

/** Footer-left button that plays the feature tutorial video (same pattern as the
 *  Bulk Include Cities dialog's help-video button). */
const HowItWorksButton = memo(() => {
  const videoState = useBoolean();
  return (
    <>
      <Button
        variant="outlined"
        color="default"
        size="small"
        startIcon={<Icon name="play-circle-outline" />}
        onClick={videoState.onTrue}
      >
        How it works
      </Button>
      <VideoModal
        state={videoState}
        videoUrl={HOW_TO_USE_SEARCH_PAYLOAD_VIDEO_URL}
        title="How it works"
      />
    </>
  );
});
HowItWorksButton.displayName = 'HowItWorksButton';

/**
 * Two tabs: "In-App Payload" (editable readable JSON — Copy + Apply) and
 * "API Payload" (the search as a public AI Ark API request — read-only, Copy
 * only). An info alert above the content explains the active tab and its
 * actions.
 */
export const SearchPayloadDialog = memo(({ state }: Props) => {
  const {
    json,
    apiMethod,
    apiUrl,
    apiDocsUrl,
    apiBodyJson,
    apiBodyCopyJson,
    apiUnsupported,
    apiLineNotes,
    apply,
    matchesCurrent,
  } = useSearchPayload();
  const { copy } = useCopyToClipboard();
  const notify = useNotify();

  const [tab, setTab] = useState<TabValue>('filters');
  const [text, setText] = useState(json);
  const [errors, setErrors] = useState<string[]>([]);

  const handleChange = (value: string) => {
    setText(value);
    if (errors.length) setErrors([]);
  };

  // Pull the line number out of a JSON parse error ("... line 52 column 5") to
  // highlight it in the editor gutter.
  const errorLine = useMemo(() => {
    const match = errors[0]?.match(/line (\d+)/i);
    return match ? Number(match[1]) : undefined;
  }, [errors]);

  /** Deduped so "Labels" doesn't appear twice when it lands on both entities. */
  const unsupportedLabels = useMemo(
    () => Array.from(new Set(apiUnsupported.map(({ gap }) => gap.label))),
    [apiUnsupported]
  );

  const copyText = async (value: string, removedCount: number) => {
    const ok = await copy(value);
    if (!ok) {
      notify.error('Could not copy to clipboard.');
      return;
    }
    // Never report a bare "Copied" when the clipboard differs from what's on
    // screen — the whole point of the tab is that the two now diverge.
    notify.success(
      removedCount > 0
        ? `Copied — ${removedCount} app-only ${
            removedCount === 1 ? 'field' : 'fields'
          } removed.`
        : 'Copied to clipboard!'
    );
  };

  const handleApply = () => {
    const result = apply(text);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors([]);
    notify.success('Search payload applied.');
    state.onFalse();
  };

  return (
    <Dialog open={state.value} onClose={state.onFalse} fullWidth maxWidth="sm">
      <DialogTitle
        title="Search Payload"
        icon="code-outline"
        onClose={state.onFalse}
      />

      <DialogContent>
        <Stack spacing={1.5} sx={SX.content}>
          <Tabs value={tab} onChange={(_, value: TabValue) => setTab(value)}>
            <Tab label="In-App Payload" value="filters" />
            <Tab label="API Payload" value="api" />
          </Tabs>

          <Alert severity="info">{ALERT_TEXT[tab](apiDocsUrl)}</Alert>

          {tab === 'api' && (
            <Alert severity="warning">
              <span>
                {'Highlighted fields are app-only — the API has no equivalent, ' +
                  'so Copy Payload leaves them out. '}
                {/* The consequence, not the removal, is what users miss: a
                    stripped payload still runs — it just matches more records. */}
                <strong>
                  Expect the API to return different, usually broader, results.
                </strong>
                {unsupportedLabels.length > 0 && (
                  <>
                    <br />
                    <strong>In this payload: </strong>
                    {unsupportedLabels.join(', ')}.
                  </>
                )}
              </span>
            </Alert>
          )}

          {tab === 'api' && (
            <Stack sx={SX.methodRow}>
              <Label
                children={
                  <Typography variant="body5" fontWeight={600} sx={SX.method}>
                    {apiMethod}
                  </Typography>
                }
                size="xsmall"
                color="default"
              />
              <Label
                children={
                  <Typography variant="body4" fontWeight={600} sx={SX.url}>
                    {apiUrl}
                  </Typography>
                }
                size="xsmall"
                color="default"
                fullWidth
                variant="outlined"
                sx={SX.urlLabel}
              />
            </Stack>
          )}

          <Box sx={SX.editorWrapper}>
            {tab === 'filters' ? (
              <JsonEditor
                fillHeight
                value={text}
                onChange={handleChange}
                error={errors.length > 0}
                errorLine={errorLine}
                ariaLabel="In-App payload JSON"
              />
            ) : (
              <JsonEditor
                fillHeight
                readOnly
                value={apiBodyJson}
                onChange={() => undefined}
                ariaLabel="API payload JSON"
                lineNotes={apiLineNotes}
              />
            )}
          </Box>

          {tab === 'filters' && errors.length > 0 && (
            <Box>
              {errors.map((err, i) => (
                <Typography key={i} variant="body2" sx={SX.error}>
                  • {err}
                </Typography>
              ))}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Box sx={SX.prepend}>
          <HowItWorksButton />
        </Box>
        <Button
          variant="outlined"
          color="default"
          size="small"
          onClick={() =>
            tab === 'filters'
              ? copyText(text, 0)
              : copyText(apiBodyCopyJson, apiUnsupported.length)
          }
        >
          Copy Payload
        </Button>
        {tab === 'filters' && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={!text.trim() || matchesCurrent(text)}
            onClick={handleApply}
          >
            Apply Search
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

SearchPayloadDialog.displayName = 'SearchPayloadDialog';
